import { FC, useState, useMemo } from 'react';
import { CircleAlert, CheckCircle2 } from 'lucide-react';
import { GoalReviewItem } from '@new/common/ui/GoalReviewItem/GoalReviewItem';
import { GeneralNotes } from '@new/common/ui/GeneralNotes/GeneralNotes';
import { Button } from '@new/common/ui/Button/Button';

export interface ReviewFormGoal {
  id: number;
  text: string;
}

export interface ReviewFormGradeData {
  goalId: number;
  rating: number;
  note: string | null;
}

export interface ReviewFormSubmitData {
  grades: ReviewFormGradeData[];
  generalNote: string;
  approved: boolean;
}

interface ReviewFormProps {
  goals: ReviewFormGoal[];
  initialRatings?: Record<number, number>;
  initialGoalNotes?: Record<number, string | null>;
  initialGeneralNote?: string;
  disabled?: boolean;
  onSubmit: (data: ReviewFormSubmitData) => void;
}

export const ReviewForm: FC<ReviewFormProps> = ({
  goals,
  initialRatings = {},
  initialGoalNotes = {},
  initialGeneralNote = '',
  disabled,
  onSubmit,
}) => {
  const [ratings, setRatings] = useState<Record<number, number>>(initialRatings);
  const [goalNotes, setGoalNotes] = useState<Record<number, string | null>>(initialGoalNotes);
  const [generalNote, setGeneralNote] = useState(initialGeneralNote);
  const [submitted, setSubmitted] = useState(false);

  const canSubmit = useMemo(
    () => goals.length > 0 && goals.every((g) => (ratings[g.id] ?? 0) === 5),
    [goals, ratings]
  );

  const allGoalsRated = useMemo(
    () => goals.length > 0 && goals.every((g) => (ratings[g.id] ?? 0) > 0),
    [goals, ratings]
  );

  const allBelowFiveHaveNotes = useMemo(
    () => goals.every((g) => (ratings[g.id] ?? 0) === 5 || !!goalNotes[g.id]),
    [goals, ratings, goalNotes]
  );

  const isReadyToSubmit = useMemo(
    () => allGoalsRated && !!generalNote.trim() && allBelowFiveHaveNotes,
    [allGoalsRated, generalNote, allBelowFiveHaveNotes]
  );

  // const averageRating = useMemo(() => {
  //   if (goals.length === 0) return 0;
  //   const total = goals.reduce((sum, g) => sum + (ratings[g.id] ?? 0), 0);
  //   return total / goals.length;
  // }, [goals, ratings]);

  const onRatingChange = (goalId: number, value: number) => {
    setRatings((prev) => ({ ...prev, [goalId]: value }));
  };

  const onGoalNoteChange = (goalId: number, value: string) => {
    setGoalNotes((prev) => ({ ...prev, [goalId]: value || null }));
  };

  const onSubmitClick = () => {
    onSubmit({
      grades: goals.map((g) => ({
        goalId: g.id,
        rating: ratings[g.id] ?? 0,
        note: goalNotes[g.id] ?? null,
      })),
      generalNote,
      approved: canSubmit,
    });
    setSubmitted(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <section className="flex flex-col gap-2">
        <p className="m-0! [font-size:var(--fs-l4)]! font-semibold text-(--neutral-black-NB900)!">
          Goals Evaluation
        </p>
        <div className="flex flex-col gap-2">
          {goals.map((goal) => (
            <GoalReviewItem
              key={goal.id}
              goal={goal}
              rating={ratings[goal.id] ?? 0}
              note={goalNotes[goal.id] ?? ''}
              disabled={disabled || submitted}
              onRatingChange={onRatingChange}
              onNoteChange={onGoalNoteChange}
            />
          ))}
        </div>
      </section>

      <GeneralNotes
        value={generalNote}
        disabled={disabled || submitted}
        onChange={setGeneralNote}
      />
      {/* 
      <div className="flex flex-col gap-3">
        <p className="font-bold text-(--neutral-black-NB800)! [font-family:var(--font-arvo)]! [font-size:var(--fs-l3)]! m-0!">
          Average
        </p>
        <div className="flex items-center gap-2">
          <StarRating
            value={Math.round(averageRating)}
            disabled
            onChange={() => { }}
          />
          <span className="font-semibold text-(--neutral-black-NB800)! m-0!">
            {averageRating > 0 ? averageRating.toFixed(1) : '---'}
          </span>
        </div>
      </div> */}

      {!submitted && (
        <section className="flex gap-2 bg-(--warning-W200) border border-(--primary-O500) rounded-[10px] p-3">
          <CircleAlert className="w-10 h-6 text-(--primary-O1000)" />
          <p className="m-0! [font-size:var(--fs-l5)]! leading-[125%] font-medium text-(--warning-W700)">
            All goals must be rated 5/5 to approve. If any goal is rated lower, the student will be
            prompted to review your feedback before submitting a revision.
          </p>
        </section>
      )}

      {submitted ? (
        <div className="flex items-center gap-3 bg-[#E6F7EF] border border-[#47B881] rounded-[10px] p-3">
          <CheckCircle2 className="w-5 h-5 text-[#47B881] shrink-0" />
          <p className="m-0! [font-size:var(--fs-l5)]! font-medium text-[#276749]">
            Review submitted successfully. The student will be notified.
          </p>
        </div>
      ) : (
        <Button variant="primary" onClick={onSubmitClick} disabled={disabled || !isReadyToSubmit}>
          Submit
        </Button>
      )}
    </div>
  );
};
